import {common, priority} from '@priority-software/priority-proto-api/lib';
import {observable, action, makeObservable, reaction} from 'mobx';
import {Screen} from './Screen';
import {Form} from './Form';
import {FormsCacheData} from './FormsCache';
import {findFormByLevel} from './utils';
import {FormattedKeyboardEvent} from '../../features/Macro/utils/macroEventFormats';
import {MacroItem} from '../../features/Macro/types';
import {systemLang} from '../../Localization/languages';
import {formsStore as legacyFormsStore} from '../../_legacy/Form/stores/FormsStore';
import {TFormSystemFlags} from '../../features/Form/actions/formSystemValues';
import {
    isCurrentEntityNetForm,
    isCurrentEntityReactForm
} from '../../_shared/GWTBridge/invoke/genPage';

export class FormsStore {
    screens: Map<string, Screen> = new Map();
    cachedData = new FormsCacheData();
    sysConstants: {[k: string]: common.IConstant};
    systemLangs: systemLang[] = [];
    htmlSignature: priority.netitems.form.IHtmlSignatureBase = null;
    formSystemFlags: TFormSystemFlags = {};

    recordingMacro: Partial<MacroItem> = null;

    @observable macros: MacroItem[] = [];
    @observable showMacroButtons: boolean = false;
    @observable isRecording: boolean = false;
    @observable currentSession: string;
    @observable onBackground: boolean;
    @observable newFormSidePanelSubforms: {[key: string]: string};
    @observable legacyFormSidePanelSubforms: {[key: string]: string};
    @observable designPanelOpened: boolean;

    constructor() {
        makeObservable(this);

        // React to subforms design changes and reset newFormSidePanelSubforms
        reaction(
            () => this.getActiveForm()?.formDesign?.subformsDesign,
            () => {
                const activeForm = this.getActiveForm();
                if (activeForm) {
                    this.newFormSidePanelSubforms = activeForm.sidePanelSubforms;
                }
            }
        );
    }

    get sidePanelSubforms() {
        if (isCurrentEntityReactForm()) {
            return this.newFormSidePanelSubforms;
        } else if (isCurrentEntityNetForm()) {
            return this.legacyFormSidePanelSubforms;
        } else {
            return null;
        }
    }

    @action
    setLegacyFormSidePanelSubforms = (subforms: {[key: string]: string}) => {
        this.newFormSidePanelSubforms = null;
        this.legacyFormSidePanelSubforms = subforms;
    };

    @action
    setIsRecording = (isRecording: boolean) => {
        this.isRecording = isRecording;
    };

    @action
    setShowMacroButtons = (show: boolean) => {
        this.showMacroButtons = show;
    };

    setRecordingMacro = ({title, shortCode, isRecursive = false}: Partial<MacroItem>) => {
        this.recordingMacro = {
            title: title,
            eventArray: [],
            shortCode: shortCode,
            isRecursive: isRecursive
        };
    };

    setEventForRecordingMacro = (event: FormattedKeyboardEvent) => {
        if (this.isRecording && this.recordingMacro) {
            this.recordingMacro.eventArray.push(event);
        }
    };

    removeRecordingMacro = () => {
        this.recordingMacro = null;
    };

    setMacros(macros: MacroItem[]) {
        this.macros = macros.slice().sort((a, b) => a.title.localeCompare(b.title));
    }

    addMacro(macro: MacroItem) {
        const sameNameMacroIndex = this.macros?.findIndex(item => item.title === macro.title);
        if (sameNameMacroIndex === -1) {
            this.macros.push(macro);
            this.macros = this.macros.slice().sort((a, b) => a.title.localeCompare(b.title));
        } else {
            this.macros[sameNameMacroIndex] = {
                ...macro,
                id: this.macros[sameNameMacroIndex].id
            };
        }
    }

    removeMacro(macroID: string) {
        const macros = this.macros.filter(macro => macro.id !== macroID);
        this.setMacros(macros);
    }

    addForm = (key: string, value: Screen) => {
        this.screens.set(key, value);
    };

    deleteForm = (session: string, clearCurrentSession: boolean = true) => {
        this.screens.delete(session);
        this.cachedData.clearSessionCache(session);
        if (clearCurrentSession && session === this.currentSession) {
            this.setCurrentSession(null);
        }
    };

    get currentScreen(): Screen {
        return this.screens?.get(this.currentSession);
    }

    get currentForm(): Form {
        return this.currentScreen?.form;
    }

    @action
    setCurrentSession = (session: string) => {
        if (session) {
            legacyFormsStore.setCurrentForm(null, null);
        }
        this.currentSession = session;
    };

    getScreenBySession = (session: string): Screen => {
        return this.screens?.get(session);
    };

    getFormBySession = (session: string): Form => {
        return this.screens?.get(session)?.form;
    };

    getActiveForm = (): Form => {
        return this.currentScreen?.getActiveForm();
    };

    getFormByLevel = (session: string, level: number) => {
        const form = this.getFormBySession(session);
        if (form) {
            return findFormByLevel(form, level);
        }
        return null;
    };

    setSystemConstants = (constants: {[k: string]: common.IConstant}): void => {
        this.sysConstants = constants;
    };

    @action
    setNewSession = (session: string) => {
        this.onBackground = false;
        this.currentSession = session;
    };

    @action
    setDesignPanelOpen = () => {
        this.designPanelOpened = true;
    };
}

export const formsStore = new FormsStore();
